// ============================================================
// PAGE HANDLE → CLASS ON <main> AND <body>
//
// Reads window.location.pathname, strips any locale prefix, then
// extracts the resource type (products / collections / pages / etc.)
// and the handle (last path segment).
//
// Classes added to both #MainContent (<main>) and <body>:
//   page-type-{type}          e.g. page-type-products
//   page-handle-{handle}      e.g. page-handle-cup-of-ambition-dark-roast-k-cup-pods
//   page-{type}-{handle}      e.g. page-products-cup-of-ambition-dark-roast-k-cup-pods
//
// This lets custom.css target any specific page without touching
// Liquid, creating new templates, or editing the parent theme.
// Example usage:
//   #MainContent.page-handle-cup-of-ambition-dark-roast-k-cup-pods .price { … }
//   body.page-handle-cup-of-ambition-dark-roast-k-cup-pods .banner { … }
// ============================================================
(function () {
  'use strict';

  // Known Shopify resource path segments (locale prefixes are stripped first)
  var RESOURCE_TYPES = ['products', 'collections', 'pages', 'blogs', 'articles', 'policies'];

  function slugify(str) {
    return str
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function applyPageClasses() {
    var main = document.getElementById('MainContent');
    var body = document.body;
    if (!main && !body) return;

    // Split pathname into non-empty segments
    var segments = window.location.pathname.split('/').filter(Boolean);
    if (!segments.length) return;

    // Strip locale prefix (e.g. "en-gb", "fr") — a locale segment contains
    // a letter followed by optional region code and never matches a resource type.
    var resourceType = '';
    var handle = '';
    var startIndex = 0;

    // Walk segments to find the first known resource type
    for (var i = 0; i < segments.length; i++) {
      if (RESOURCE_TYPES.indexOf(segments[i]) !== -1) {
        resourceType = segments[i];
        // Handle is the segment immediately after the resource type
        handle = segments[i + 1] ? slugify(segments[i + 1]) : '';
        startIndex = i;
        break;
      }
    }

    // Fallback: if no known resource type found, use last segment as handle
    if (!resourceType && segments.length) {
      handle = slugify(segments[segments.length - 1]);
    }

    var classes = [];
    if (resourceType) classes.push('page-type-' + resourceType);
    if (handle)       classes.push('page-handle-' + handle);
    if (resourceType && handle) classes.push('page-' + resourceType + '-' + handle);

    // ── Cup of Ambition — scalable brand theming ──────────────────────────
    // Any product/collection whose URL contains "cup-of-ambition" gets:
    //   1. A shared .cup-of-ambition class (the scalable CSS hook).
    //   2. A back-compat alias of the original page-scoped class so every
    //      existing CoA CSS rule applies to ALL CoA products/collections —
    //      not just the original test product.
    // classList.add() de-dupes, so the original test product is unaffected.
    // Type-aware: product URLs get the product alias, collection URLs get
    // the collection alias — product-only and collection-only rules stay
    // correctly separated.
    if (/cup-of-ambition/.test(window.location.pathname)) {
      classes.push('cup-of-ambition');
      if (resourceType === 'products') {
        classes.push('page-handle-cup-of-ambition-dark-roast-k-cup-pods');
      } else if (resourceType === 'collections') {
        classes.push('page-collections-dollys-cup-of-ambition');
      }
    }

    classes.forEach(function (cls) {
      if (main) main.classList.add(cls);
      if (body) body.classList.add(cls);
    });
  }

  // Run as early as possible — before DOMContentLoaded if the DOM is ready,
  // otherwise queue it. The script is defer-loaded so the DOM is always ready
  // by the time this runs, but guard just in case.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyPageClasses);
  } else {
    applyPageClasses();
  }
})();

// ============================================================
// Smooth announcement-bar offset for the homepage overlay header.
// --header-top tracks the bar's live bottom edge as it scrolls, clamped at 0.
// This eliminates the gap+snap caused by a static top offset.
(function () {
  if (!document.body.classList.contains('homepage') &&
      !document.body.classList.contains('life-of-many-colors') &&
      !document.body.classList.contains('smokey-mountain-dna')) return;
  var bar = document.querySelector('.utility-bar');
  if (!bar) return;
  var root = document.documentElement;
  function update() {
    root.style.setProperty('--announcement-bar-h', bar.offsetHeight + 'px');
    root.style.setProperty('--header-top', Math.max(0, bar.getBoundingClientRect().bottom) + 'px');
  }
  update();
  window.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update);
  window.addEventListener('load', update);
})();

// ============================================================
// CONTENT CAROUSEL — RELIABLE ARROW DISABLE + VERTICAL CENTERING
//
// Strategy: IntersectionObserver (IO) watching the first and last
// .slider__slide within the scroll track. IO fires reliably after
// scroll-snap settles — independent of scroll-event timing — so
// "on the last slide → next disabled" is guaranteed every time.
//
// .carousel-arrow--disabled is the single source of truth for the
// faded/non-interactive state (CSS targets this class only, not
// [disabled], so Dawn's page-math can never cause false fades).
//
// Vertical centering: re-run after every image load so arrows sit
// at the midpoint of the card image on every breakpoint.
// ============================================================
(function () {
  'use strict';

  // ── Toggle disabled state (class is the CSS hook; attribute for a11y) ─────
  function setDisabled(btn, isDisabled) {
    if (isDisabled) {
      btn.classList.add('carousel-arrow--disabled');
      btn.setAttribute('aria-disabled', 'true');
    } else {
      btn.classList.remove('carousel-arrow--disabled');
      btn.removeAttribute('aria-disabled');
    }
  }

  // ── Arrow vertical centering ───────────────────────────────────────────────
  function centerArrows(component) {
    var prevBtn = component.querySelector('button[name="previous"]');
    var nextBtn = component.querySelector('button[name="next"]');
    if (!prevBtn || !nextBtn) return;

    var firstSlide = component.querySelector('.slider__slide');
    if (!firstSlide) return;

    var cardInner = firstSlide.querySelector('.card__inner');
    if (!cardInner) return;

    var imageHeight = cardInner.offsetHeight;
    if (imageHeight === 0) return;

    var btnHeight = prevBtn.getBoundingClientRect().height || 44;
    var topOffset = Math.round((imageHeight / 2) - (btnHeight / 2));
    prevBtn.style.top = topOffset + 'px';
    nextBtn.style.top = topOffset + 'px';
  }

  // ── Initialise each content-carousel slider-component ─────────────────────
  function initCarousel(component) {
    if (component.dataset.customArrowsInit) return;
    component.dataset.customArrowsInit = '1';

    var sliderEl = component.querySelector('[id^="Slider-"]');
    var prevBtn  = component.querySelector('button[name="previous"]');
    var nextBtn  = component.querySelector('button[name="next"]');
    if (!sliderEl || !prevBtn || !nextBtn) return;

    var slides = Array.prototype.slice.call(component.querySelectorAll('.slider__slide'));
    if (!slides.length) return;

    var firstSlide = slides[0];
    var lastSlide  = slides[slides.length - 1];

    // Track live intersection ratios for first and last slides
    var firstRatio = 0;
    var lastRatio  = 0;

    // Apply disabled state based on current ratios + scrollLeft fallback
    function apply() {
      var sl        = sliderEl.scrollLeft;
      var maxScroll = sliderEl.scrollWidth - sliderEl.clientWidth;

      // Non-scrollable (only 1 card, or track not wide enough)
      if (maxScroll <= 2) {
        setDisabled(prevBtn, true);
        setDisabled(nextBtn, true);
        return;
      }

      // Prev: disabled when first slide is fully visible OR at scroll start
      setDisabled(prevBtn, firstRatio >= 0.98 || sl <= 2);

      // Next: disabled when last slide is fully visible OR at scroll end
      setDisabled(nextBtn, lastRatio >= 0.98 || sl >= maxScroll - 2);
    }

    // IntersectionObserver — root is the scroll track so ratios are
    // relative to the visible scroll port, not the viewport.
    // threshold array gives us fine-grained ratio updates.
    var thresholds = [];
    for (var i = 0; i <= 100; i++) { thresholds.push(i / 100); }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.target === firstSlide) firstRatio = entry.intersectionRatio;
        if (entry.target === lastSlide)  lastRatio  = entry.intersectionRatio;
      });
      apply();
    }, {
      root: sliderEl,
      threshold: thresholds
    });

    io.observe(firstSlide);
    io.observe(lastSlide);

    // Also run apply() on scroll (catches sub-pixel cases IO misses)
    // and re-asserts our class over Dawn's [disabled] toggling.
    sliderEl.addEventListener('scroll', apply, { passive: true });

    // Re-check on resize
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        apply();
        centerArrows(component);
      }, 100);
    });

    // Initial state
    apply();

    // Arrow vertical centering — run now and after every image load
    centerArrows(component);
    component.querySelectorAll('img').forEach(function (img) {
      if (img.complete) {
        centerArrows(component);
      } else {
        img.addEventListener('load', function () { centerArrows(component); });
      }
    });
  }

  // ── Wait for slider-components to be defined and connected ────────────────
  function initAll() {
    document.querySelectorAll(
      'slider-component[data-slider-type="content-carousel"]'
    ).forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  window.addEventListener('load', initAll);

  if (window.MutationObserver) {
    var mo = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('slider-component[data-slider-type="content-carousel"]')) {
            initCarousel(node);
          }
          node.querySelectorAll && node.querySelectorAll(
            'slider-component[data-slider-type="content-carousel"]'
          ).forEach(initCarousel);
        });
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }
})();

/* ============================================================
   TASTING NOTES — CDN icon mapping
   The parent-theme Liquid sets --tasting-notes-icon-url via
   asset_url (tasting-note-{handle}.svg). Those assets get
   overwritten on every parent-theme push, so icons never render.
   This script reads each note's label text, maps it to the
   correct Shopify CDN URL, and overrides the CSS variable on
   the icon span — the existing mask + pink-color CSS does the rest.
   Runs on DOMContentLoaded + shopify:section:load (theme editor).
   ============================================================ */
(function () {
  'use strict';

  /* Full CDN URLs keyed by handleized note name.
     Handles both the exact metafield choice values and common aliases. */
  var ICON_MAP = {
    /* ── Current metafield choice list ── */
    'vanilla':               'https://cdn.shopify.com/s/files/1/1001/8324/5081/files/icon--vanilla-tasting-note-icon.svg?v=1790111252',
    'cocoa-nib':             'https://cdn.shopify.com/s/files/1/1001/8324/5081/files/icon--cocoa-tasting-note-icon.svg?v=1790111251',
    'stone-fruit':           'https://cdn.shopify.com/s/files/1/1001/8324/5081/files/icon--stone-fruit-tasting-note-icon.svg?v=1790111252',
    'milk-chocolate':        'https://cdn.shopify.com/s/files/1/1001/8324/5081/files/icon--dark-chocolate-tasting-note-icon.svg?v=1790111252',
    'bittersweet-chocolate': 'https://cdn.shopify.com/s/files/1/1001/8324/5081/files/icon--dark-chocolate-tasting-note-icon.svg?v=1790111252',
    'berry':                 'https://cdn.shopify.com/s/files/1/1001/8324/5081/files/icon--berry-tasting-note-icon.svg?v=1790111252',
    'caramel':               'https://cdn.shopify.com/s/files/1/1001/8324/5081/files/icon--caramel-tasting-note-icon.svg?v=1790111251',
    'cherry':                'https://cdn.shopify.com/s/files/1/1001/8324/5081/files/icon--cherry-tasting-note-icon.svg?v=1790111252',
    /* ── Aliases / future additions ── */
    'cocoa':                 'https://cdn.shopify.com/s/files/1/1001/8324/5081/files/icon--cocoa-tasting-note-icon.svg?v=1790111251',
    'dark-chocolate':        'https://cdn.shopify.com/s/files/1/1001/8324/5081/files/icon--dark-chocolate-tasting-note-icon.svg?v=1790111252',
    'chocolate':             'https://cdn.shopify.com/s/files/1/1001/8324/5081/files/icon--dark-chocolate-tasting-note-icon.svg?v=1790111252',
    'caramelized-sugar':     'https://cdn.shopify.com/s/files/1/1001/8324/5081/files/icon--caramelized-sugar-tasting-note-icon.svg?v=1790111252',
    'figs':                  'https://cdn.shopify.com/s/files/1/1001/8324/5081/files/icon--figs-tasting-note-icon.svg?v=1790111252',
    'fig':                   'https://cdn.shopify.com/s/files/1/1001/8324/5081/files/icon--figs-tasting-note-icon.svg?v=1790111252',
    'honey':                 'https://cdn.shopify.com/s/files/1/1001/8324/5081/files/icon--honey-tasting-note-icon.svg?v=1790111252',
    'smoky':                 'https://cdn.shopify.com/s/files/1/1001/8324/5081/files/icon--smoky-tasting-note-icon.svg?v=1790111252',
    'toffee':                'https://cdn.shopify.com/s/files/1/1001/8324/5081/files/icon--toffee-tasting-note-icon.svg?v=1790111252',
    'golden-raisin':         'https://cdn.shopify.com/s/files/1/1001/8324/5081/files/icon--golden-raisin-tasting-note-icon.svg?v=1790111252',
    'sweet-molasses':        'https://cdn.shopify.com/s/files/1/1001/8324/5081/files/icon--sweet-molasses-tasting-note-icon.svg?v=1790111252',
    'molasses':              'https://cdn.shopify.com/s/files/1/1001/8324/5081/files/icon--sweet-molasses-tasting-note-icon.svg?v=1790111252',
    'hazelnut':              'https://cdn.shopify.com/s/files/1/1001/8324/5081/files/icon--hazelnut-tasting-note-icon.svg?v=1790111252',
    'sweet-smoke':           'https://cdn.shopify.com/s/files/1/1001/8324/5081/files/icon--sweet-smoke-tasting-note-icon.svg?v=1790111252',
    'cedar':                 'https://cdn.shopify.com/s/files/1/1001/8324/5081/files/icon--cedar-tasting-note-icon.svg?v=1790111251',
    'warm-spice':            'https://cdn.shopify.com/s/files/1/1001/8324/5081/files/icon--warm-spice-tasting-note-icon.svg?v=1790111251',
    'spice':                 'https://cdn.shopify.com/s/files/1/1001/8324/5081/files/icon--warm-spice-tasting-note-icon.svg?v=1790111251',
    'nutty':                 'https://cdn.shopify.com/s/files/1/1001/8324/5081/files/icon--nutty-tasting-note-icon.svg?v=1790111251'
  };

  function handleize(str) {
    return str.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  function applyTastingNoteIcons() {
    document.querySelectorAll('.tasting-notes__item').forEach(function (item) {
      var labelEl = item.querySelector('.tasting-notes__item-label');
      var iconEl  = item.querySelector('.tasting-notes__icon');
      if (!labelEl || !iconEl) return;

      var handle = handleize(labelEl.textContent || '');
      var url    = ICON_MAP[handle];

      if (url) {
        iconEl.style.setProperty('--tasting-notes-icon-url', "url('" + url + "')");
        iconEl.style.display = '';
      } else {
        /* No matching icon — hide the span so no solid-color square appears */
        iconEl.style.display = 'none';
      }
    });
  }

  /* Run on initial load */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyTastingNoteIcons);
  } else {
    applyTastingNoteIcons();
  }

  /* Re-run when Shopify theme editor reloads a section */
  document.addEventListener('shopify:section:load', applyTastingNoteIcons);
})();

// ============================================================
// LOGO SWAP — Cup of Ambition pages only
// Replaces the header logo (desktop + mobile) with the black
// Cup of Ambition wordmark on:
//   /products/cup-of-ambition-dark-roast-k-cup-pods
//   /collections/dollys-cup-of-ambition
// Clears srcset/sizes so the browser can't re-pick the original.
// Sets height:auto to preserve the new logo's aspect ratio.
// deploy-safe: custom.js only, no Liquid changes.
// // ============================================================
// (function () {
//   'use strict';
//   var NEW_LOGO = 'https://cdn.shopify.com/s/files/1/1001/8324/5081/files/dolly-logo-pink.svg?v=1790276008';

//   function onTargetPage() {
//     var b = document.body;
//     if (!b) return false;
//     if (
//       b.classList.contains('page-handle-cup-of-ambition-dark-roast-k-cup-pods') ||
//       b.classList.contains('page-collections-dollys-cup-of-ambition')
//     ) return true;
//     // Pathname fallback — runs before class-adder fires
//     return /\/(products\/cup-of-ambition-dark-roast-k-cup-pods|collections\/dollys-cup-of-ambition)(\/|$)/.test(window.location.pathname);
//   }

//   function swapLogo() {
//     if (!onTargetPage()) return;
//     document.querySelectorAll('.header__heading-logo').forEach(function (img) {
//       img.removeAttribute('srcset');
//       img.removeAttribute('sizes');
//       img.src = NEW_LOGO;
//       img.style.height = 'auto';
//       img.style.maxWidth = '200px';
//       img.style.width = '100%';
//     });
//   }

//   if (document.readyState === 'loading') {
//     document.addEventListener('DOMContentLoaded', swapLogo);
//   } else {
//     swapLogo();
//   }
//   // Re-run after theme editor section reloads
//   document.addEventListener('shopify:section:load', swapLogo);
// })();

// ============================================================
// AUTHENTICITY BLOCK IMAGE SWAP — Cup of Ambition pages only
// The image lives inside a <picture> with <source> elements that
// override <img src> on tablet/desktop, so we remove the sources
// (and clear srcset/sizes) before setting the new src.
// Scalable: fires on ANY URL containing "cup-of-ambition" so all
// CoA products get the swap, not just the original test product.
// deploy-safe: custom.js only, no Liquid changes.
// ============================================================
(function () {
  'use strict';
  var NEW_IMG = 'https://cdn.shopify.com/s/files/1/1001/8324/5081/files/dolly-merch.png?v=1790279393';

  function onTargetPage() {
    // Primary: check the shared .cup-of-ambition class added by the page-class IIFE
    if (document.body && document.body.classList.contains('cup-of-ambition')) return true;
    // Fallback: pathname check (runs before class-adder fires on very fast loads)
    return /cup-of-ambition/.test(window.location.pathname);
  }

  function swapAuthenticityImg() {
    if (!onTargetPage()) return;
    document.querySelectorAll('.authenticity-logo-image').forEach(function (img) {
      var picture = img.closest('picture');
      if (picture) {
        picture.querySelectorAll('source').forEach(function (s) { s.remove(); });
      }
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = NEW_IMG;
      img.style.height = 'auto';
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', swapAuthenticityImg);
  } else {
    swapAuthenticityImg();
  }
  document.addEventListener('shopify:section:load', swapAuthenticityImg);
})();
